import React, { useState, useEffect, useRef, useMemo } from 'react'
import injectStyles from 'react-jss'
import moment from 'moment'
import DisplayMap from 'Components/GMap/DisplayMap'
import { TYPES } from 'Components/GMap/DisplayMap/const'
import { getMissionsForCity } from './graphql'
import LiveMapFilters, {
  LiveMissions,
  YesterdayMissions,
  TomorrowMissions,
  DEFAULT_FILTERS,
  TodayMission,
} from '../LiveMap/filters'
import { MISSION_STATUS_ENUM, MISSION_ENUM_TYPES } from '../../shared/constants'
import { getMissionActivities } from 'Module/graphQL'
import {
  getMissionMarker,
  getMissionTypeSpecificProps,
  getMissionPath,
  getMissionMarkerWithTabs,
  getLocation,
  formatFilterDate,
} from './utils'
import { styles } from './styled'
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator'
import { Checkbox } from '../Form'
import { getInfoWindowContent } from '../GMap/DisplayMap/util'

const DEFAULT_TRACKS = {
  [MISSION_ENUM_TYPES.HANDTOHAND]: true,
  [MISSION_ENUM_TYPES.LETTERBOX]: true,
}

const fetchCityAndActivities = async (
  { cityId, campaignId },
  filters,
  tracksOptions = DEFAULT_TRACKS,
) => {
  const { startDate, endDate } = filters

  filters.cityId = cityId
  if (campaignId) {
    filters.campaignId = campaignId
  }

  const response = await getMissionsForCity(filters)
  const result = await response.json()

  if (result['errorMessage']) throw new Error(result['errorMessage'])
  if (result.errors && result.errors[0])
    throw new Error(result.errors[0].message)

  const misssionsEdges = result.data.city.missions.edges

  const missionsLastActivity = []
  const missionsAllActivity = []
  const missionsMap = misssionsEdges.reduce((acc, m) => {
    acc[m.node.id] = { node: m.node }

    if (
      tracksOptions[MISSION_ENUM_TYPES.HANDTOHAND] &&
      m.node.type === MISSION_ENUM_TYPES.HANDTOHAND
    ) {
      missionsAllActivity.push(m.node.id)
    } else if (
      tracksOptions[MISSION_ENUM_TYPES.LETTERBOX] &&
      m.node.type === MISSION_ENUM_TYPES.LETTERBOX
    ) {
      missionsAllActivity.push(m.node.id)
    } else {
      missionsLastActivity.push(m.node.id)
    }
    return acc
  }, {})

  const [lastActivities, activities] = await Promise.all([
    getBAActivities(missionsLastActivity, +startDate, +endDate, true),
    getBAActivities(missionsAllActivity, +startDate, +endDate, false),
  ])

  lastActivities.forEach(activity => {
    if (missionsMap[activity.missionId]) {
      missionsMap[activity.missionId].node.lastActivity = activity
    }
  })

  activities.forEach(activity => {
    if (missionsMap[activity.missionId]) {
      missionsMap[activity.missionId].node.activities = missionsMap[
        activity.missionId
      ].node.activities
        ? [...missionsMap[activity.missionId].node.activities, activity]
        : [activity]
    }
  })

  result.data.city.missions.edges = Object.values(missionsMap)

  return result.data.city
}

const getBAActivities = async (
  missionIds,
  startTimestamp,
  endTimestamp,
  onlyLastPoint,
) => {
  try {
    const mapTrailblazerNodeToActivity = f => ({
      datetime: f.properties.timestamp,
      latitude: f.geometry.coordinates[1],
      longitude: f.geometry.coordinates[0],
      speed: f.properties.speed,
      missionId: f.properties.missionId,
    })

    const reqVars = {
      missions: missionIds,
      dateRange: { from: startTimestamp, to: endTimestamp },
      onlyLastPoint,
    }
    let response = await getMissionActivities(reqVars)

    let result = await response.json()

    if (result['errorMessage'] || !result.data || !result.data.searchFeatures) {
      throw new Error(result['errorMessage'])
    }

    let formattedActivities = result.data.searchFeatures.collection.features.map(
      mapTrailblazerNodeToActivity,
    )

    while (result.data.searchFeatures.pageInfo.hasNextPage) {
      reqVars.after = result.data.searchFeatures.pageInfo.endCursor
      response = await getMissionActivities(reqVars)

      result = await response.json()
      formattedActivities = formattedActivities.concat(
        result.data.searchFeatures.collection.features.map(
          mapTrailblazerNodeToActivity,
        ),
      )
    }

    formattedActivities = formattedActivities.sort(
      (activityA, activityB) =>
        new Date(activityA.datetime) - new Date(activityB.datetime),
    )

    return formattedActivities
  } catch (e) {
    console.error(e)
    return []
  }
}

const LiveMapContainer = ({
  cityId: defaultCityId,
  campaignId,
  clients,
  cities,
  classes,
}) => {
  const [city, setCity] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState(DEFAULT_FILTERS(defaultCityId))
  const [showZones, setShowZones] = useState(false)
  const [showTracks, setShowTracks] = useState(DEFAULT_TRACKS)
  const intervalRef = useRef(null)
  const mapRef = useRef(null)
  const openedWindowRef = useRef({
    openedWindows: [],
    opened: false,
  })

  const { cityId } = filters

  useEffect(() => {
    if (!cityId) {
      return
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    updateData(true)
    intervalRef.current = setInterval(updateData, 60 * 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [cityId, campaignId, filters, showTracks])

  const { polygons, markers, paths } = useMemo(() => {
    if (!city || !city.missions.edges) {
      return {}
    }
    const { polygons, paths, locations } = city.missions.edges.reduce(
      (acc, edge) => {
        const { pathColor, polygon } = getMissionTypeSpecificProps(edge.node)

        if (showZones) {
          if (polygon) {
            acc.polygons.push(polygon)
          }
        }

        if (
          (showTracks[MISSION_ENUM_TYPES.HANDTOHAND] &&
            edge.node.type === MISSION_ENUM_TYPES.HANDTOHAND) ||
          (showTracks[MISSION_ENUM_TYPES.LETTERBOX] &&
            edge.node.type === MISSION_ENUM_TYPES.LETTERBOX)
        ) {
          const path = getMissionPath({
            ...edge.node,
            pathColor,
          })

          if (path) {
            acc.paths.push(path)
          }
        }

        const location = getLocation(edge.node)

        if (location) {
          const key = `${location.lat}-${location.lng}`

          acc.locations[key] = acc.locations[key]
            ? [...acc.locations[key], edge.node]
            : [edge.node]
        }

        return acc
      },
      { polygons: [], paths: [], locations: {} },
    )

    const markers = Object.values(locations).reduce((acc, missions) => {
      if (missions.length === 1) {
        const marker = getMissionMarker(missions[0], cityId)

        if (marker) {
          acc.push(marker)
        }
      } else {
        acc.push(getMissionMarkerWithTabs(missions, cityId))
      }

      return acc
    }, [])

    return {
      polygons,
      markers,
      paths,
    }
  }, [city, showZones])

  const openMarkerModal = marker => {
    const map = mapRef.current
    const { openedWindows } = openedWindowRef.current
    if (map && filters.tableSearch) {
      const {
        options: { infoWindowSettings },
        position,
        customProperties,
      } = marker

      map.setCenter(position)

      const infoWindow = new google.maps.InfoWindow({
        ...infoWindowSettings,
        position: position,
        content: getInfoWindowContent({
          type: TYPES.MARKER,
          clickCoords: position,
          fallbackContent: infoWindowSettings.content,
        }),
        customProperties,
      })

      infoWindow.open(map)
      openedWindowRef.current = {
        opened: true,
        openedWindows: [...openedWindows, infoWindow],
      }
    }
  }

  const closeMarkerModal = () => {
    const { opened, openedWindows } = openedWindowRef.current
    if (opened) {
      openedWindows.forEach(w => w.close())
      openedWindowRef.current = {
        opened: false,
        openedWindows: [],
      }
    }
  }

  const prepareFilters = filters => {
    const presetFilter = {
      startDate: formatFilterDate(moment().startOf('day')),
      endDate: formatFilterDate(moment().endOf('day')),
      status: [
        MISSION_STATUS_ENUM.COMPLETED,
        MISSION_STATUS_ENUM.BOOKED,
        MISSION_STATUS_ENUM.CHECKED_IN,
        MISSION_STATUS_ENUM.CANCELLED,
      ],
    }

    switch (filters.time) {
      case TodayMission:
        presetFilter.status = [
          MISSION_STATUS_ENUM.COMPLETED,
          MISSION_STATUS_ENUM.BOOKED,
          MISSION_STATUS_ENUM.CHECKED_IN,
        ]
        break
      case LiveMissions:
        presetFilter.startDate = formatFilterDate(moment().subtract(3, 'hours'))
        presetFilter.endDate = formatFilterDate(moment().add(3, 'hours'))
        presetFilter.status = [MISSION_STATUS_ENUM.CHECKED_IN]
        break
      case YesterdayMissions:
        presetFilter.startDate = formatFilterDate(
          moment()
            .subtract(1, 'day')
            .startOf('day'),
        )

        presetFilter.endDate = formatFilterDate(
          moment()
            .subtract(1, 'day')
            .endOf('day'),
        )
        presetFilter.status = [
          MISSION_STATUS_ENUM.COMPLETED,
          MISSION_STATUS_ENUM.BOOKED,
          MISSION_STATUS_ENUM.UNDER_REVIEW,
          MISSION_STATUS_ENUM.CANCELLED,
        ]
        break
      case TomorrowMissions:
        presetFilter.startDate = formatFilterDate(
          moment()
            .add(1, 'day')
            .startOf('day'),
        )
        presetFilter.endDate = formatFilterDate(
          moment()
            .add(1, 'day')
            .endOf('day'),
        )
        break
    }
    const { startDate, endDate, status } = presetFilter

    const convertedFilters = {
      startDate,
      endDate,
      statuses: filters['status'] ? [filters['status']] : status,
      filter: filters['tableSearch'],
      types: filters['type'] || [
        MISSION_ENUM_TYPES.HANDTOHAND,
        MISSION_ENUM_TYPES.LETTERBOX,
      ],
    }

    if (filters['client']) {
      convertedFilters.clientId = filters['client']
    }

    return convertedFilters
  }

  const updateData = async (setLoading = false) => {
    if (!cityId) {
      return
    }

    if (setLoading) {
      setIsLoading(true)
    }
    const result = await fetchCityAndActivities(
      { cityId, campaignId },
      prepareFilters(filters),
      showTracks,
    )

    setCity(result)

    if (setLoading) {
      closeMarkerModal()
      if (result.missions.edges.length === 1) {
        const marker = getMissionMarker(result.missions.edges[0].node, cityId)
        openMarkerModal(marker)
      } else if (result.missions.edges.length && filters.tableSearch) {
        result.missions.edges.forEach((edge, i) => {
          const marker = getMissionMarker(result.missions.edges[i].node, cityId)
          openMarkerModal(marker)
        })
      } else if (mapRef.current) {
        const { latitude, longitude } = result
        mapRef.current.setCenter({
          lat: latitude,
          lng: longitude,
        })
      }

      setIsLoading(false)
    }
  }

  const BANames = useMemo(() => {
    if (!city || !city.missions) {
      return []
    }
    return city.missions.edges.reduce((acc, edge) => {
      const name = `${edge.node.user.firstName} ${edge.node.user.lastName}`

      if (!acc.includes(name)) {
        acc.push(name)
      }

      return acc
    }, [])
  }, [city])

  const onChangeFilters = async filters => {
    setFilters(filters)
  }

  if (!cityId) {
    return <div className={classes.liveMapWrapper}>No city to show a map</div>
  }

  if (!city) {
    return (
      <div className={classes.loadingWrapper}>
        <LoadingIndicator color="inherit" />
      </div>
    )
  }

  const { latitude, longitude } = city

  return (
    <div className={classes.liveMapWrapper}>
      <LiveMapFilters
        onChange={filters => {
          onChangeFilters(filters)
        }}
        filters={filters}
        clients={clients}
        cities={cities}
        suggestions={BANames}
      />
      <div>
        <DisplayMap
          onMapLoad={(google, { map }) => {
            const styleOptions = [
              {
                featureType: 'poi',
                elementType: 'labels',

                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
              {
                featureType: 'transit',
                elementType: 'labels',

                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
            ]

            map.setOptions({
              styles: styleOptions,
            })
            mapRef.current = map
            map.setCenter({
              lat: latitude,
              lng: longitude,
            })
          }}
          markers={markers}
          polygons={showZones ? polygons : []}
          paths={paths}
        />
      </div>

      <div className={classes.checkboxRow}>
        <Checkbox
          className="livemap-checkbox"
          label="Display LB mission zones"
          input={{
            value: showZones,
            onChange: () => {
              setShowZones(!showZones)
            },
          }}
        />

        <Checkbox
          className="livemap-checkbox"
          label="Display tracks LB"
          input={{
            value: showTracks[MISSION_ENUM_TYPES.LETTERBOX],
            onChange: () => {
              setShowTracks({
                ...showTracks,
                [MISSION_ENUM_TYPES.LETTERBOX]: !showTracks[
                  MISSION_ENUM_TYPES.LETTERBOX
                ],
              })
            },
          }}
        />

        <Checkbox
          className="livemap-checkbox"
          label="Display track H2H"
          input={{
            value: showTracks[MISSION_ENUM_TYPES.HANDTOHAND],
            onChange: () => {
              setShowTracks({
                ...showTracks,
                [MISSION_ENUM_TYPES.HANDTOHAND]: !showTracks[
                  MISSION_ENUM_TYPES.HANDTOHAND
                ],
              })
            },
          }}
        />
      </div>

      {isLoading && (
        <div className={classes.loadingWrapper}>
          <LoadingIndicator color="inherit" />
        </div>
      )}
    </div>
  )
}

export default injectStyles(styles)(LiveMapContainer)
